import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Users, Settings, BarChart3, Shield, LogOut, FileText, Globe } from 'lucide-react';
import { adminService } from '../services/api';

const Admin = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [systemStats, setSystemStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is admin and redirect if not
  useEffect(() => {
    if (user && !(user?.is_admin === true || user?.is_admin === "true" || user?.is_admin === 1)) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
  };

  const fetchSystemStats = async () => {
    try {
      setLoading(true);
      const response = await adminService.getSystemStats();
      setSystemStats(response.stats || response);
    } catch (error) {
      console.error('Error fetching system stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemStats();
  }, []);

  const adminSections = [
    {
      title: 'User Management',
      description: 'Manage users, view profiles, and control access',
      icon: Users,
      link: '/admin/users',
      color: 'bg-blue-500',
      hoverColor: 'hover:bg-blue-600'
    },
    {
      title: 'System Statistics',
      description: 'View system analytics and performance metrics',
      icon: BarChart3,
      link: '/admin/stats',
      color: 'bg-green-500',
      hoverColor: 'hover:bg-green-600'
    },
    {
      title: 'Content Moderation',
      description: 'Review and moderate public content',
      icon: Shield,
      link: '/admin/moderation',
      color: 'bg-yellow-500',
      hoverColor: 'hover:bg-yellow-600'
    },
    {
      title: 'System Settings',
      description: 'Configure system-wide settings and preferences',
      icon: Settings,
      link: '/admin/settings',
      color: 'bg-purple-500',
      hoverColor: 'hover:bg-purple-600'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <Link to="/" className="flex flex-col items-start hover:opacity-80 transition-opacity">
                <img src="/big-clipboard-text-logo.jpg" alt="Big Clipboard Logo" className="h-10 w-auto" />
                <span className="text-sm text-primary-700 font-medium mt-1">Copy Once. Use Anywhere.</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="btn-secondary flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                Dashboard
              </Link>
              <Link to="/public" className="btn-secondary flex items-center">
                <Globe className="h-4 w-4 mr-2" />
                Public
              </Link>
              <button
                onClick={handleLogout}
                className="btn-secondary flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage your Big Clipboard application</p>
        </div>

        {/* Welcome Message */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <Settings className="h-6 w-6 text-red-600" />
              </div>
            </div>
            <div className="ml-4">
              <h2 className="text-lg font-medium text-gray-900">Welcome, {user?.full_name}</h2>
              <p className="text-gray-600">You have administrator privileges. Use the sections below to manage the application.</p>
            </div>
          </div>
        </div>

        {/* Admin Sections Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {adminSections.map((section) => {
            const IconComponent = section.icon;
            return (
              <Link
                key={section.title}
                to={section.link}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200"
              >
                <div className="flex items-center mb-4">
                  <div className={`w-10 h-10 ${section.color} rounded-lg flex items-center justify-center`}>
                    <IconComponent className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="ml-3 text-lg font-medium text-gray-900">{section.title}</h3>
                </div>
                <p className="text-gray-600 text-sm">{section.description}</p>
              </Link>
            );
          })}
        </div>

        {/* Quick Stats */}
        <div className="mt-12">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="h-8 w-8 text-blue-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Users</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {loading ? '...' : (systemStats?.total_users || '--')}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Items</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {loading ? '...' : (systemStats?.total_items || '--')}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Globe className="h-8 w-8 text-purple-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Public Items</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {loading ? '...' : (systemStats?.public_items || '--')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin; 